import { useMutation, useQuery } from "convex/react";
import { useCallback, useMemo, useState } from "react";

import { REQUESTABLE_APP_IDS } from "@/../convex/_lib/appAccess";
import { api } from "@/../convex/_generated/api";

/**
 * Couche d'accès aux Applications (côté client).
 *
 * Branche le backend `convex/appAccess.ts` (table `appAccess`) : statut
 * d'accès du user courant par app + wrapper de la demande d'accès. C'est ce
 * qui transforme le gate front-only historique (email propriétaire en dur)
 * en un vrai droit accordable (demande → approbation, ou don direct owner).
 */

export type AppAccessStatus = "pending" | "approved" | "denied" | "revoked";

/**
 * Apps qui supportent le flux « demande d'accès » (= visibilité
 * `request_access`).
 *
 * 🩸 C'était une COPIE, figée à `["twitter"]`, avec en commentaire « garder les
 * deux synchronisés » — et personne ne les a synchronisées. Le 27-08-2026, la
 * source Convex en portait trois (`twitter`, `twitter-sites`, `telegram-sites`)
 * et celle-ci une seule. Conséquence : une app de site verrouillée s'affichait
 * « indisponible » au lieu de proposer « Demander l'accès », sans qu'aucune
 * erreur ne le dise (cf `resolveBuiltinAppAction` et `DesktopSurface`, partis le 08-10-2026).
 *
 * Elle se DÉRIVE maintenant de la source. C'est le même défaut que la copie à
 * la main des perm-sets de rôles, retirée la veille : une copie qui ne casse
 * pas quand elle est fausse ne protège rien — et un commentaire qui demande de
 * la tenir à jour n'est pas un mécanisme.
 */
export { REQUESTABLE_APP_IDS };

export function isRequestableAppId(appId: string): boolean {
  return REQUESTABLE_APP_IDS.has(appId);
}

/**
 * Mes accès aux apps (statut par appId). Réactif via Convex. Pendant le
 * bootstrap (query non résolue) → map vide + `isLoading: true`. Les appelants
 * ne doivent PAS bloquer les capabilities de base là-dessus (cf
 * `useCapabilities` qui n'OR le grant que pour Twitter).
 */
export function useMyAppAccess(): {
  byApp: Map<string, AppAccessStatus>;
  isLoading: boolean;
} {
  const mine = useQuery(api.appAccess.myAccess, {});
  return useMemo(() => {
    if (mine === undefined) {
      return { byApp: new Map<string, AppAccessStatus>(), isLoading: true };
    }
    const byApp = new Map<string, AppAccessStatus>();
    for (const r of mine.rows) byApp.set(r.appId, r.status as AppAccessStatus);
    return { byApp, isLoading: false };
  }, [mine]);
}

/** True si l'app `appId` m'est accordée (status `approved`). */
export function useHasApprovedAppAccess(appId: string): boolean {
  const { byApp } = useMyAppAccess();
  return byApp.get(appId) === "approved";
}

/**
 * Wrapper de la mutation « demander l'accès ». Expose l'appId en cours de
 * requête pour qu'un bouton affiche son état pending sans état global.
 */
export function useRequestAppAccess(): {
  request: (appId: string, message?: string) => Promise<void>;
  requestingAppId: string | null;
} {
  const requestAccess = useMutation(api.appAccess.requestAccess);
  const [requestingAppId, setRequestingAppId] = useState<string | null>(null);
  const request = useCallback(
    async (appId: string, message?: string) => {
      setRequestingAppId(appId);
      try {
        await requestAccess({ appId, message });
      } finally {
        setRequestingAppId(null);
      }
    },
    [requestAccess],
  );
  return { request, requestingAppId };
}
